import React, { useEffect } from 'react';
import { Button, Row, Col } from 'antd';
import { motion, useAnimation } from 'framer-motion';
import { ArrowRightOutlined } from '@ant-design/icons';
import '../styles/AppleStyle.css';

const cardSurface: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 16,
  padding: 20,
  border: '1px solid #d2d2d7',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
  textAlign: 'center',
};

const AppleHero: React.FC = () => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="home" className="hero-apple">
      <div className="container-wide" style={{ position: 'relative', zIndex: 2 }}>
        <Row align="middle" gutter={[48, 48]}>
          <Col xs={24} lg={12}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={controls}
              className="hero-content"
              style={{ textAlign: 'left' }}
            >
              <motion.div variants={itemVariants} transition={{ duration: 0.8 }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#ffffff',
                    padding: '6px 16px',
                    borderRadius: 980,
                    marginBottom: 24,
                    border: '1px solid #d2d2d7',
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      backgroundColor: '#30d158',
                      borderRadius: '50%',
                    }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0071e3' }}>
                    专业美国空派服务
                  </span>
                </div>
              </motion.div>

              <motion.h1
                variants={itemVariants}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="display-large"
                style={{ marginBottom: 24, color: '#1d1d1f' }}
              >
                专业空派
                <br />
                直达美国
              </motion.h1>

              <motion.p
                variants={itemVariants}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="body-large"
                style={{ marginBottom: 28, color: '#6e6e73' }}
              >
                从中国到美国，<strong style={{ color: '#1d1d1f', fontWeight: 600 }}>5-8天稳定时效</strong>
                的专业空派服务。
                <br />
                整合航线资源，优化清关流程，为跨境电商提供可靠的物流解决方案。
              </motion.p>

              <motion.div
                variants={itemVariants}
                transition={{ duration: 0.8, delay: 0.3 }}
                style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<ArrowRightOutlined />}
                  iconPosition="end"
                  onClick={() => {
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    background: '#0071e3',
                    border: 'none',
                    borderRadius: 8,
                    height: 52,
                    padding: '0 24px',
                    fontSize: 17,
                    fontWeight: 600,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  立即获取报价
                </Button>

                <Button
                  size="large"
                  onClick={() => {
                    document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    background: '#fafafc',
                    border: '1px solid #d2d2d7',
                    borderRadius: 8,
                    height: 52,
                    padding: '0 24px',
                    fontSize: 17,
                    fontWeight: 500,
                    color: '#0071e3',
                  }}
                >
                  智能计算运费
                </Button>
              </motion.div>

              <motion.div
                variants={itemVariants}
                transition={{ duration: 0.8, delay: 0.4 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 32,
                  flexWrap: 'wrap',
                  fontSize: 14,
                  color: '#6e6e73',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#30d158', fontSize: 16 }}>✓</span>
                  无隐藏费用
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#30d158', fontSize: 16 }}>✓</span>
                  全程可追踪
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#30d158', fontSize: 16 }}>✓</span>
                  专业客服支持
                </div>
              </motion.div>
            </motion.div>
          </Col>

          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              style={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              <div style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                  {[
                    { icon: '⚡', value: '5-8天', label: '稳定时效' },
                    { icon: '📦', value: '0.5kg', label: '最低起收' },
                    { icon: '✅', value: '97.2%', label: '清关成功率' },
                    { icon: '🛡️', value: '超时赔付', label: '服务保障' },
                  ].map((item, i) => (
                    <Col xs={24} sm={12} key={item.label}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 + i * 0.1 }}
                        style={cardSurface}
                      >
                        <div style={{ fontSize: '2rem', marginBottom: 8 }}>{item.icon}</div>
                        <div
                          style={{
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: '#1d1d1f',
                            marginBottom: 4,
                          }}
                        >
                          {item.value}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6e6e73' }}>{item.label}</div>
                      </motion.div>
                    </Col>
                  ))}
                </Row>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  style={{
                    marginTop: 20,
                    padding: '16px 20px',
                    background: '#ffffff',
                    borderRadius: 12,
                    border: '1px solid #d2d2d7',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: '#1d1d1f',
                      marginBottom: 4,
                    }}
                  >
                    覆盖全美 50 州
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6e6e73' }}>
                    LAX·SFO·JFK·ORD 四大机场清关
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default AppleHero;
